/**
 * ============================================================
 * HOBBLIVISION EVIDENCE RESOLVER V1
 * ============================================================
 *
 * Purpose:
 *
 *   Resolve noisy observations into known database entities.
 *
 * This is intentionally NOT a "name cleaner".
 *
 * The resolver attempts to answer:
 *
 *   "Given one or more noisy observations and a universe of
 *    known values, which known value is best supported?"
 *
 * The same machinery can eventually resolve:
 *
 *   - name
 *   - brand
 *   - set
 *   - year
 *   - card number
 *   - variation
 *
 * Only the field-specific configuration should change.
 *
 * Design principles:
 *
 *   - No hard-coded OCR corrections
 *   - No entity-specific replacements
 *   - Multiple observations can reinforce one another
 *   - Exact matches are strongest
 *   - Partial/token matches are useful
 *   - OCR noise is treated as similarity, not as a dictionary
 *   - Database frequency is supporting evidence, not identity
 *   - Alternatives are preserved
 *   - "No database match" does NOT mean "no evidence"
 *
 * ============================================================
 */

/**
 * ============================================================
 * CONFIGURATION
 * ============================================================
 */

const ENTITY_CONFIG = {
    name: {
        minObservationLength: 3,

        weights: {
            characterSimilarity: 0.35,
            tokenSimilarity: 0.30,
            containment: 0.15,
            observationSupport: 0.15,
            databaseFrequency: 0.05,
        },

        strongMatchThreshold: 0.82,
        minimumCandidateScore: 0.45,
    },

    brand: {
        minObservationLength: 2,

        weights: {
            characterSimilarity: 0.45,
            tokenSimilarity: 0.25,
            containment: 0.10,
            observationSupport: 0.15,
            databaseFrequency: 0.05,
        },

        strongMatchThreshold: 0.82,
        minimumCandidateScore: 0.45,
    },

    set: {
        minObservationLength: 3,

        weights: {
            characterSimilarity: 0.25,
            tokenSimilarity: 0.30,
            containment: 0.15,
            observationSupport: 0.15,
            databaseFrequency: 0.05,
        },

        strongMatchThreshold: 0.82,
        minimumCandidateScore: 0.45,
    },

    year: {
        minObservationLength: 4,

        weights: {
            characterSimilarity: 0.55,
            tokenSimilarity: 0.10,
            containment: 0.10,
            observationSupport: 0.20,
            databaseFrequency: 0.05,
        },

        strongMatchThreshold: 0.90,
        minimumCandidateScore: 0.60,
    },

    cardNumber: {
        minObservationLength: 1,

        weights: {
            characterSimilarity: 0.45,
            tokenSimilarity: 0.20,
            containment: 0.15,
            observationSupport: 0.15,
            databaseFrequency: 0.05,
        },

        strongMatchThreshold: 0.88,
        minimumCandidateScore: 0.50,
    },

    variation: {
        minObservationLength: 2,

        weights: {
            characterSimilarity: 0.35,
            tokenSimilarity: 0.30,
            containment: 0.15,
            observationSupport: 0.15,
            databaseFrequency: 0.05,
        },

        strongMatchThreshold: 0.82,
        minimumCandidateScore: 0.45,
    },
};

const DEFAULT_ENTITY_TYPE = "name";

/**
 * ============================================================
 * NORMALIZATION
 * ============================================================
 *
 * Normalization removes formatting differences.
 *
 * It intentionally does NOT perform semantic corrections.
 *
 * We do NOT want:
 *
 *   OO → R
 *   0 → O
 *   1 → I
 *
 * Those are OCR-specific guesses.
 *
 * Similarity scoring should determine whether two strings are
 * plausibly related.
 */

function normalize(value) {
    return String(value || "")
        .toUpperCase()
        .replace(/[^\w\s'-]/g, " ")
        .replace(/_/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function normalizeCompact(value) {
    return normalize(value).replace(/[\s'-]/g, "");
}

function tokenize(value) {
    return normalize(value)
        .split(" ")
        .map((token) => token.trim())
        .filter(Boolean);
}

/**
 * ============================================================
 * BASIC STRING DISTANCE
 * ============================================================
 */

/**
 * Standard Levenshtein distance.
 *
 * Used as a generic character-level similarity mechanism.
 */
function levenshteinDistance(a, b) {
    const left = normalizeCompact(a);
    const right = normalizeCompact(b);

    if (!left) {
        return right.length;
    }

    if (!right) {
        return left.length;
    }

    const previous = new Array(right.length + 1);

    for (let j = 0; j <= right.length; j++) {
        previous[j] = j;
    }

    for (let i = 1; i <= left.length; i++) {
        let current = new Array(right.length + 1);

        current[0] = i;

        for (let j = 1; j <= right.length; j++) {
            const substitutionCost =
                left[i - 1] === right[j - 1]
                    ? 0
                    : 1;

            current[j] = Math.min(
                current[j - 1] + 1,
                previous[j] + 1,
                previous[j - 1] + substitutionCost
            );
        }

        for (let j = 0; j <= right.length; j++) {
            previous[j] = current[j];
        }
    }

    return previous[right.length];
}

/**
 * Convert edit distance into a 0-1 similarity score.
 */
function characterSimilarity(a, b) {
    const left = normalizeCompact(a);
    const right = normalizeCompact(b);

    if (!left || !right) {
        return 0;
    }

    if (left === right) {
        return 1;
    }

    const distance =
        levenshteinDistance(left, right);

    const maxLength =
        Math.max(left.length, right.length);

    if (!maxLength) {
        return 0;
    }

    return Math.max(
        0,
        1 - distance / maxLength
    );
}

/**
 * ============================================================
 * TOKEN SIMILARITY
 * ============================================================
 */

function tokenSimilarity(a, b) {
    const leftTokens = tokenize(a);
    const rightTokens = tokenize(b);

    if (!leftTokens.length || !rightTokens.length) {
        return 0;
    }

    let matchedScore = 0;

    for (const leftToken of leftTokens) {
        let best = 0;

        for (const rightToken of rightTokens) {
            best = Math.max(
                best,
                characterSimilarity(
                    leftToken,
                    rightToken
                )
            );
        }

        matchedScore += best;
    }

    const leftAverage =
        matchedScore / leftTokens.length;

    let reverseMatchedScore = 0;

    for (const rightToken of rightTokens) {
        let best = 0;

        for (const leftToken of leftTokens) {
            best = Math.max(
                best,
                characterSimilarity(
                    rightToken,
                    leftToken
                )
            );
        }

        reverseMatchedScore += best;
    }

    const rightAverage =
        reverseMatchedScore / rightTokens.length;

    return (
        leftAverage + rightAverage
    ) / 2;
}

/**
 * ============================================================
 * CONTAINMENT
 * ============================================================
 *
 * Useful for observations such as:
 *
 *   "ROME ODUNZE"
 *   "ODUNZE"
 *
 * or:
 *
 *   "DONRUSS FOOTBALL"
 *   "DONRUSS"
 */

function containmentScore(a, b) {
    const left = normalizeCompact(a);
    const right = normalizeCompact(b);

    if (!left || !right) {
        return 0;
    }

    if (left === right) {
        return 1;
    }

    if (
        left.includes(right) ||
        right.includes(left)
    ) {
        const shorter =
            Math.min(
                left.length,
                right.length
            );

        const longer =
            Math.max(
                left.length,
                right.length
            );

        return shorter / longer;
    }

    return 0;
}

/**
 * ============================================================
 * OBSERVATION NORMALIZATION
 * ============================================================
 *
 * The resolver accepts several observation shapes so it can
 * evolve without forcing the parser to use one exact format.
 *
 * Supported examples:
 *
 *   "ROME ODUNZE"
 *
 *   {
 *      value: "ROME ODUNZE",
 *      confidence: 0.91,
 *      source: "ocr"
 *   }
 */

function normalizeObservation(observation) {
    if (
        typeof observation === "string"
    ) {
        return {
            value: observation,
            confidence: 1,
            source: "unknown",
        };
    }

    if (!observation) {
        return null;
    }

    const value =
        observation.value ??
        observation.text ??
        observation.observation ??
        "";

    if (!String(value).trim()) {
        return null;
    }

    let confidence =
        Number(
            observation.confidence
        );

    if (!Number.isFinite(confidence)) {
        confidence = 1;
    }

    confidence =
        Math.max(
            0,
            Math.min(1, confidence)
        );

    return {
        ...observation,
        value: String(value),
        confidence,
        source:
            observation.source ||
            "unknown",
    };
}

/**
 ============================================================
 * OBSERVATION DEDUPLICATION
 * ============================================================
 */

function deduplicateObservations(
    observations
) {
    const map = new Map();

    for (const rawObservation of observations || []) {
        const observation =
            normalizeObservation(
                rawObservation
            );

        if (!observation) {
            continue;
        }

        const normalized =
            normalize(observation.value);

        if (!normalized) {
            continue;
        }

        const existing =
            map.get(normalized);

        if (!existing) {
            map.set(
                normalized,
                observation
            );

            continue;
        }

        /**
         * Preserve the strongest confidence.
         */
        if (
            observation.confidence >
            existing.confidence
        ) {
            map.set(
                normalized,
                observation
            );
        }
    }

    return [...map.values()];
}

/**
 * ============================================================
 * DATABASE VALUE NORMALIZATION
 * ============================================================
 *
 * Database values may be:
 *
 *   "Rome Odunze"
 *
 * or:
 *
 *   {
 *      value: "Rome Odunze",
 *      count: 17
 *   }
 */

function normalizeDatabaseValue(value) {
    if (
        typeof value === "string" ||
        typeof value === "number"
    ) {
        return {
            value: String(value),
            frequency: 1,
        };
    }

    if (!value) {
        return null;
    }

    const resolvedValue =
        value.value ??
        value.name ??
        value.text ??
        "";

    if (!String(resolvedValue).trim()) {
        return null;
    }

    let frequency =
        Number(
            value.frequency ??
            value.count ??
            value.usageCount ??
            1
        );

    if (
        !Number.isFinite(frequency) ||
        frequency < 1
    ) {
        frequency = 1;
    }

    return {
        ...value,
        value: String(resolvedValue),
        frequency,
    };
}

/**
 ============================================================
 * DATABASE FREQUENCY
 * ============================================================
 *
 * Frequency is deliberately capped.
 *
 * A common value should receive some supporting evidence,
 * but popularity must never overwhelm similarity.
 */

function calculateFrequencyScores(
    values
) {
    const normalized =
        values.map(
            normalizeDatabaseValue
        ).filter(Boolean);

    const maxFrequency =
        Math.max(
            1,
            ...normalized.map(
                (item) => item.frequency
            )
        );

    return normalized.map(
        (item) => ({
            ...item,
            frequencyScore:
                Math.log1p(
                    item.frequency
                ) /
                Math.log1p(
                    maxFrequency
                ),
        })
    );
}

/**
 * ============================================================
 * OBSERVATION → CANDIDATE COMPARISON
 * ============================================================
 */

function compareObservationToCandidate(
    observation,
    candidate
) {
    const character =
        characterSimilarity(
            observation.value,
            candidate.value
        );

    const tokens =
        tokenSimilarity(
            observation.value,
            candidate.value
        );

    const containment =
        containmentScore(
            observation.value,
            candidate.value
        );

    return {
        characterSimilarity: character,
        tokenSimilarity: tokens,
        containment,
    };
}

/**
 * ============================================================
 * OBSERVATION SUPPORT
 * ============================================================
 *
 * If multiple independent observations point toward the same
 * candidate, confidence should increase.
 *
 * We intentionally use diminishing returns so:
 *
 *   1 observation = meaningful
 *   2 observations = stronger
 *   10 duplicate observations ≠ 10x confidence
 */

function calculateObservationSupport(
    candidate,
    observations
) {
    if (
        !observations?.length
    ) {
        return 0;
    }

    const scores = [];

    for (const observation of observations) {
        const comparison =
            compareObservationToCandidate(
                observation,
                candidate
            );

        const score =
            (
                comparison.characterSimilarity *
                    0.50
            ) +
            (
                comparison.tokenSimilarity *
                    0.35
            ) +
            (
                comparison.containment *
                    0.15
            );

        /**
         * Weight the observation by the confidence supplied
         * by the upstream OCR/parser layer.
         */
        scores.push(
            score *
            observation.confidence
        );
    }

    if (!scores.length) {
        return 0;
    }

    scores.sort(
        (a, b) => b - a
    );

    /**
     * Weighted diminishing-return support.
     */
    const weights = [
        1.00,
        0.65,
        0.40,
        0.25,
        0.15,
    ];

    let weightedTotal = 0;
    let weightTotal = 0;

    for (
        let index = 0;
        index < scores.length;
        index++
    ) {
        const weight =
            weights[index] ??
            0.10;

        weightedTotal +=
            scores[index] * weight;

        weightTotal += weight;
    }

    if (!weightTotal) {
        return 0;
    }

    return Math.min(
        1,
        weightedTotal / weightTotal
    );
}

/**
 * ============================================================
 * CANDIDATE SCORE
 * ============================================================
 */

function scoreCandidate(
    candidate,
    observations,
    config
) {
    if (
        !candidate?.value ||
        !observations?.length
    ) {
        return {
            score: 0,
            bestObservation: null,
            observationSupport: 0,
            comparisons: [],
        };
    }

    const comparisons =
        observations.map(
            (observation) => ({
                observation,
                ...compareObservationToCandidate(
                    observation,
                    candidate
                ),
            })
        );

    /**
     * The strongest individual observation is important.
     *
     * This prevents several weak observations from dragging a
     * genuinely strong exact observation downward.
     */
    const bestComparison =
        comparisons.reduce(
            (best, current) => {
                const currentScore =
                    (
                        current.characterSimilarity *
                            0.50
                    ) +
                    (
                        current.tokenSimilarity *
                            0.35
                    ) +
                    (
                        current.containment *
                            0.15
                    );

                const bestScore =
                    best
                        ? (
                            best.characterSimilarity *
                                0.50
                        ) +
                        (
                            best.tokenSimilarity *
                                0.35
                        ) +
                        (
                            best.containment *
                                0.15
                        )
                        : -1;

                return currentScore >
                    bestScore
                    ? current
                    : best;
            },
            null
        );

    const bestIndividualScore =
        bestComparison
            ? (
                bestComparison.characterSimilarity *
                    0.50
            ) +
            (
                bestComparison.tokenSimilarity *
                    0.35
            ) +
            (
                bestComparison.containment *
                    0.15
            )
            : 0;

    const observationSupport =
        calculateObservationSupport(
            candidate,
            observations
        );

    const frequencyScore =
        Number(
            candidate.frequencyScore
        ) || 0;

    const score =
        (
            bestComparison?.characterSimilarity ||
            0
        ) *
            config.weights.characterSimilarity +

        (
            bestComparison?.tokenSimilarity ||
            0
        ) *
            config.weights.tokenSimilarity +

        (
            bestComparison?.containment ||
            0
        ) *
            config.weights.containment +

        observationSupport *
            config.weights.observationSupport +

        frequencyScore *
            config.weights.databaseFrequency;

    return {
        score: Math.max(
            0,
            Math.min(1, score)
        ),

        bestIndividualScore,

        bestObservation:
            bestComparison
                ? bestComparison.observation
                : null,

        observationSupport,

        comparisons,
    };
}

/**
 * ============================================================
 * CANDIDATE DEDUPLICATION
 * ============================================================
 */

function deduplicateDatabaseValues(
    values
) {
    const map = new Map();

    for (const rawValue of values || []) {
        const normalized =
            normalizeDatabaseValue(
                rawValue
            );

        if (!normalized) {
            continue;
        }

        const key =
            normalize(
                normalized.value
            );

        if (!key) {
            continue;
        }

        const existing =
            map.get(key);

        if (!existing) {
            map.set(
                key,
                normalized
            );

            continue;
        }

        /**
         * Keep the version with the highest frequency.
         */
        if (
            normalized.frequency >
            existing.frequency
        ) {
            map.set(
                key,
                normalized
            );
        }
    }

    return calculateFrequencyScores(
        [...map.values()]
    );
}

/**
 * ============================================================
 * RESOLVE ENTITY
 * ============================================================
 *
 * Main public API.
 *
 * Example:
 *
 * resolveEntity({
 *     type: "name",
 *
 *     observations: [
 *         {
 *             value: "OO ODUNZE",
 *             confidence: 0.61,
 *             source: "ocr"
 *         },
 *         {
 *             value: "ROME ODUNZE",
 *             confidence: 0.92,
 *             source: "ocr"
 *         }
 *     ],
 *
 *     databaseValues: [
 *         "Rome Odunze",
 *         "Rome Odunze Jr."
 *     ]
 * })
 */

export function resolveEntity({
    type = DEFAULT_ENTITY_TYPE,
    observations = [],
    databaseValues = [],
} = {}) {
    const config =
        ENTITY_CONFIG[type] ||
        ENTITY_CONFIG[
            DEFAULT_ENTITY_TYPE
        ];

    const normalizedObservations =
        deduplicateObservations(
            observations
        ).filter(
            (observation) =>
                normalize(
                    observation.value
                ).length >=
                config.minObservationLength
        );

    /**
     * --------------------------------------------------------
     * No observations
     * --------------------------------------------------------
     */
    if (
        !normalizedObservations.length
    ) {
        return {
            type,

            resolved: null,
            value: null,
            confidence: 0,

            observations: [],

            candidates: [],

            hasDatabaseMatch: false,
        };
    }

    const normalizedCandidates =
        deduplicateDatabaseValues(
            databaseValues
        );

    /**
     * --------------------------------------------------------
     * No database universe
     * --------------------------------------------------------
     *
     * We still return the observations.
     *
     * This is important for manual entry and future retrieval.
     */
    if (
        !normalizedCandidates.length
    ) {
        const strongestObservation =
            [...normalizedObservations]
                .sort(
                    (a, b) =>
                        b.confidence -
                        a.confidence
                )[0];

        return {
            type,

            resolved: null,
            value: null,
            confidence:
                strongestObservation
                    ?.confidence || 0,

            observations:
                normalizedObservations,

            candidates: [],

            hasDatabaseMatch: false,

            unresolvedEvidence: {
                value:
                    strongestObservation?.value ||
                    null,

                confidence:
                    strongestObservation
                        ?.confidence || 0,
            },
        };
    }

    /**
     * --------------------------------------------------------
     * Score candidates
     * --------------------------------------------------------
     */

    const scoredCandidates =
        normalizedCandidates
            .map((candidate) => {
                const scoring =
                    scoreCandidate(
                        candidate,
                        normalizedObservations,
                        config
                    );

                return {
                    value:
                        candidate.value,

                    frequency:
                        candidate.frequency,

                    frequencyScore:
                        candidate.frequencyScore,

                    score:
                        scoring.score,

                    bestIndividualScore:
                        scoring.bestIndividualScore,

                    observationSupport:
                        scoring.observationSupport,

                    bestObservation:
                        scoring
                            .bestObservation
                            ?.value ||
                        null,

                    comparisons:
                        scoring.comparisons.map(
                            (comparison) => ({
                                observation:
                                    comparison
                                        .observation
                                        .value,

                                observationConfidence:
                                    comparison
                                        .observation
                                        .confidence,

                                characterSimilarity:
                                    Number(
                                        comparison
                                            .characterSimilarity
                                            .toFixed(4)
                                    ),

                                tokenSimilarity:
                                    Number(
                                        comparison
                                            .tokenSimilarity
                                            .toFixed(4)
                                    ),

                                containment:
                                    Number(
                                        comparison
                                            .containment
                                            .toFixed(4)
                                    ),
                            })
                        ),
                };
            })
            .filter(
                (candidate) =>
                    candidate.score >=
                    config.minimumCandidateScore
            )
            .sort(
                (a, b) =>
                    b.score -
                    a.score
            );

    /**
     * --------------------------------------------------------
     * No sufficiently strong database candidate
     * --------------------------------------------------------
     */
    if (
        !scoredCandidates.length
    ) {
        const strongestObservation =
            [...normalizedObservations]
                .sort(
                    (a, b) =>
                        b.confidence -
                        a.confidence
                )[0];

        return {
            type,

            resolved: null,
            value: null,
            confidence:
                strongestObservation
                    ?.confidence || 0,

            observations:
                normalizedObservations,

            candidates: [],

            hasDatabaseMatch: false,

            unresolvedEvidence: {
                value:
                    strongestObservation?.value ||
                    null,

                confidence:
                    strongestObservation
                        ?.confidence || 0,
            },
        };
    }

    const primary =
        scoredCandidates[0];

    const second =
        scoredCandidates[1] ||
        null;

    /**
     * --------------------------------------------------------
     * Confidence
     * --------------------------------------------------------
     *
     * Confidence considers:
     *
     *   1. absolute candidate score
     *   2. strength of best observation
     *   3. agreement from multiple observations
     *   4. separation from the next candidate
     *
     * This is NOT intended to be a mathematically calibrated
     * probability. It is an evidence confidence score.
     */

    const scoreComponent =
        primary.score;

    const individualComponent =
        primary.bestIndividualScore;

    const supportComponent =
        primary.observationSupport;

    const separation =
        second
            ? Math.max(
                0,
                primary.score -
                    second.score
            )
            : primary.score;

    /**
     * Separation is scaled so a clear winner receives a useful
     * confidence increase without completely dominating.
     */
    const separationComponent =
        Math.min(
            1,
            separation * 2
        );

    let confidence =
        (
            scoreComponent * 0.45
        ) +
        (
            individualComponent * 0.25
        ) +
        (
            supportComponent * 0.20
        ) +
        (
            separationComponent * 0.10
        );

    /**
     * Strong exact/near-exact evidence should remain strong even
     * if another candidate is reasonably close.
     */
    if (
        primary.bestIndividualScore >=
        0.97
    ) {
        confidence =
            Math.max(
                confidence,
                0.90
            );
    }

    confidence =
        Math.max(
            0,
            Math.min(1, confidence)
        );

    /**
     * --------------------------------------------------------
     * Resolve
     * --------------------------------------------------------
     *
     * We only call something "resolved" when it clears the
     * entity-specific strong threshold.
     *
     * Otherwise the ranked candidates are retained as evidence.
     */
    const isResolved =
        primary.score >=
            config.strongMatchThreshold ||
        (
            primary.bestIndividualScore >=
                0.95 &&
            primary.observationSupport >=
                0.70
        );

    return {
        type,

        resolved:
            isResolved
                ? primary.value
                : null,

        value:
            isResolved
                ? primary.value
                : null,

        confidence,

        hasDatabaseMatch: true,

        observations:
            normalizedObservations,

        candidates:
            scoredCandidates,

        primary: {
            value:
                primary.value,

            score:
                primary.score,

            confidence,

            supportCount:
                normalizedObservations
                    .filter(
                        (observation) => {
                            const comparison =
                                primary
                                    .comparisons
                                    .find(
                                        (item) =>
                                            item.observation ===
                                            observation
                                                .value
                                    );

                            if (!comparison) {
                                return false;
                            }

                            return (
                                (
                                    comparison
                                        .characterSimilarity *
                                        0.50
                                ) +
                                (
                                    comparison
                                        .tokenSimilarity *
                                        0.35
                                ) +
                                (
                                    comparison
                                        .containment *
                                        0.15
                                )
                            ) >= 0.60;
                        }
                    ).length,

            bestObservation:
                primary.bestObservation,

            directIdentity:
                primary
                    .bestIndividualScore >=
                0.95,
        },

        alternatives:
            scoredCandidates
                .slice(1, 5)
                .map(
                    (candidate) => ({
                        value:
                            candidate.value,

                        score:
                            candidate.score,

                        support:
                            candidate
                                .observationSupport,
                    })
                ),
    };
}

/**
 * ============================================================
 * CONVENIENCE HELPERS
 * ============================================================
 *
 * These helpers keep callers readable while still using the
 * same generic resolver underneath.
 */

export function resolveName({
    observations = [],
    databaseValues = [],
} = {}) {
    return resolveEntity({
        type: "name",
        observations,
        databaseValues,
    });
}

export function resolveBrand({
    observations = [],
    databaseValues = [],
} = {}) {
    return resolveEntity({
        type: "brand",
        observations,
        databaseValues,
    });
}

export function resolveSet({
    observations = [],
    databaseValues = [],
} = {}) {
    return resolveEntity({
        type: "set",
        observations,
        databaseValues,
    });
}

export function resolveYear({
    observations = [],
    databaseValues = [],
} = {}) {
    return resolveEntity({
        type: "year",
        observations,
        databaseValues,
    });
}

export function resolveCardNumber({
    observations = [],
    databaseValues = [],
} = {}) {
    return resolveEntity({
        type: "cardNumber",
        observations,
        databaseValues,
    });
}

/**
 * ============================================================
 * DIAGNOSTIC HELPERS
 * ============================================================
 *
 * Useful while developing the HobbliVision InspectorPanel.
 */

export function compareEvidenceValues(
    observation,
    candidate
) {
    return {
        observation,
        candidate,

        characterSimilarity:
            characterSimilarity(
                observation,
                candidate
            ),

        tokenSimilarity:
            tokenSimilarity(
                observation,
                candidate
            ),

        containment:
            containmentScore(
                observation,
                candidate
            ),
    };
}

export default resolveEntity;

